import React, { useEffect, useRef, useState } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
  Marker,
  TrafficLayer,
} from '@react-google-maps/api';
import { MapPin, Car, Bike, Bus, User } from 'lucide-react';

const Maps_API_KEY = process.env.REACT_APP_Maps_API_KEY;
const containerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 6.5244, lng: 3.3792 }; // Lagos

const travelModeIcons = {
  DRIVING: <Car className="w-5 h-5" />,
  WALKING: <User className="w-5 h-5" />,
  BICYCLING: <Bike className="w-5 h-5" />,
  TRANSIT: <Bus className="w-5 h-5" />,
};

const MapComponent = ({ destinationAddress, onDirectionsReady }) => {
  const [origin, setOrigin] = useState(null);
  const [originInput, setOriginInput] = useState('');
  const [travelMode, setTravelMode] = useState('DRIVING');
  const [directions, setDirections] = useState(null);
  const [destinationPlaceId, setDestinationPlaceId] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [isManualMode, setIsManualMode] = useState(false);
  const [travelTime, setTravelTime] = useState('');

  const originAddressRef = useRef('Your Current Location');
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: Maps_API_KEY || '',
    libraries: ['places'],
  });

  useEffect(() => {
    const getUserLocation = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(defaultCenter);
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(defaultCenter),
          { enableHighAccuracy: true, timeout: 7000, maximumAge: 60000 }
        );
      });

    const init = async () => {
      const loc = await getUserLocation();
      setOrigin(loc);
      setMapCenter(loc);
      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ location: loc });
        originAddressRef.current = response.results[0]?.formatted_address || 'Unknown';
      } catch {
        originAddressRef.current = 'Unknown';
      }
    };

    if (isLoaded && !origin) init();
  }, [isLoaded, origin]);

  useEffect(() => {
    const geocodeDestination = async () => {
      if (!destinationAddress || !isLoaded || !Maps_API_KEY) return;
      try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ address: destinationAddress });
        if (response.results[0]) {
          setDestinationPlaceId(response.results[0].place_id);
        } else {
          setDirections(null);
          onDirectionsReady?.({ error: 'Destination not found.' });
        }
      } catch (e) {
        setDirections(null);
        onDirectionsReady?.({ error: 'Geocode failed.' });
      }
    };

    geocodeDestination();
  }, [destinationAddress, isLoaded]);

  useEffect(() => {
    const calculateRoute = async () => {
      if (!origin || !destinationPlaceId || !isLoaded || !window.google) return;

      const directionsService = new window.google.maps.DirectionsService();
      try {
        const result = await directionsService.route({
          origin,
          destination: { placeId: destinationPlaceId },
          travelMode: window.google.maps.TravelMode[travelMode],
          drivingOptions: {
            departureTime: new Date(), // Use current time for traffic data
            trafficModel: 'bestguess', // Traffic model: 'bestguess', 'pessimistic', or 'optimistic'
          },
        });

        if (result.status === 'OK') {
          setDirections(result);
          onDirectionsReady?.({
            directions: result,
            originAddress: originAddressRef.current,
            destinationAddress,
          });

          // Calculate travel time
          const leg = result.routes[0]?.legs[0];
          if (leg) {
            const duration = leg.duration_in_traffic || leg.duration;
            setTravelTime(duration.text);
          } else {
            setTravelTime('');
          }
        } else {
          setDirections(null);
        }
      } catch (error) {
        console.error('Directions error:', error);
        setDirections(null);
      }
    };

    calculateRoute();
  }, [origin, destinationPlaceId, travelMode, isLoaded]);

  const handleCustomOriginSubmit = async () => {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ address: originInput });
      if (response.results[0]) {
        const loc = response.results[0].geometry.location;
        setOrigin({ lat: loc.lat(), lng: loc.lng() });
        setMapCenter({ lat: loc.lat(), lng: loc.lng() });
        originAddressRef.current = response.results[0].formatted_address;
      } else {
        alert('Origin not found.');
      }
    } catch {
      alert('Failed to find origin.');
    }
  };

  const handleMarkerDragEnd = (e) => {
    const newLoc = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setOrigin(newLoc);
    setMapCenter(newLoc);
    originAddressRef.current = 'Custom Location';
  };

  if (!Maps_API_KEY) return <div className="p-4 text-yellow-600">⚠️ Missing API Key</div>;
  if (loadError) return <div className="p-4 text-red-600">❌ Failed to load maps.</div>;
  if (!isLoaded) return <div className="p-4 text-gray-600">🗺️ Loading map...</div>;

  return (
    <div className="w-full h-full flex flex-col">
      {/* Control Panel */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Get Directions</h3>
        
        <div className="flex flex sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Enter origin"
              value={originInput}
              onChange={(e) => setOriginInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
              style={{ border: '2px solid #d1d5db' }}
            />
          </div>
          <button
            onClick={handleCustomOriginSubmit}
            className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
          >
            Set Origin
          </button>
          <select
            value={travelMode}
            onChange={(e) => setTravelMode(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
          >
            {['DRIVING', 'WALKING', 'BICYCLING', 'TRANSIT'].map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
          <button
            onClick={() => setIsManualMode(!isManualMode)}
            className="px-4 py-2 border-2 border-green-600 text-green-600 font-medium rounded-lg hover:bg-green-50 transition-colors whitespace-nowrap"
          >
            {isManualMode ? 'Disable Drag' : 'Enable Drag'}
          </button>
        </div>

        {/* Travel Information */}
        <div className="flex flex-col sm:flex-row justify-between gap-4 mt-4">
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 mb-1">Origin:</p>
            <p className="text-sm text-green-600 mb-3">{originAddressRef.current}</p>
            <p className="text-sm font-bold text-gray-900 mb-1">Destination:</p>
            <p className="text-sm text-green-600">{destinationAddress || 'Not set'}</p>
          </div>
          {travelTime && (
            <div className="text-right">
              <p className="text-sm font-bold text-gray-900 mb-1">Estimated Travel Time:</p>
              <p className="text-sm text-green-600">{travelTime}</p>
            </div>
          )}
        </div>
      </div>

      {/* Google Map */}
      <div className="flex-1 rounded-xl overflow-hidden shadow-sm">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={13}
          onLoad={(map) => (mapRef.current = map)}
          options={{ streetViewControl: false }}
        >
          {origin && (
            <Marker
              position={origin}
              draggable={isManualMode}
              onDragEnd={handleMarkerDragEnd}
              label="Origin"
            />
          )}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: false,
                polylineOptions: {
                  strokeColor: '#16a34a',
                  strokeOpacity: 0.8,
                  strokeWeight: 5,
                },
              }}
            />
          )}
          <TrafficLayer />
        </GoogleMap>
      </div>
    </div>
  );
};

export default MapComponent;
